import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { CreateActorButton } from '@/components/custom-digital-human';
import { ActorCard } from './actor-card';
import { EditVideoPlayModal } from '@/components/modal-edit-video-play';
import { useUserManagementHooks } from '@/manager';

interface AllActorModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  actors: Actor[];
  voices: Audio[]
  selectedId?: string;
  onOpenChange?: (open: boolean) => void;
  onSave?: (actor: Actor) => void;
}

/** 所有演员弹窗 */
export const AllActorModal: React.FC<AllActorModalProps> = ({
  trigger,
  open: externalOpen,
  actors = [],
  voices = [],
  selectedId,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);
  const { regenerateActor } = useUserManagementHooks.useRegenerateActor();
  const { deleteActor } = useUserManagementHooks.useDeleteActor();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (!open) {
      setSelectedActorId(null);
    }
  };

  // 处理选择
  const handleActorSelect = (actor: Actor) => {
    if (actor.generateStatus !== 2) return;
    setSelectedActorId(prev => {
      if (prev === actor.id) {
        return null; // 取消选择
      } else {
        return actor.id; // 添加选择
      }
    });
  };

  const handleVideoClick = (id: string) => {
    setSelectedVideoIndex(actors.findIndex(actor => actor.id === id));
    setModalOpen(true);
  };

  const handleSave = async () => {
    handleClose();
    onSave?.(actors.find(actor => actor.id === selectedActorId));
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] h-[727px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t('agent_dashboard.user_detail.template.all_actor_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none overflow-y-auto mb-5'>
          {/* 演员区域 */}
          <div>
            <div className='grid grid-cols-6 gap-3 gap-y-12'>
              <CreateActorButton className='w-[100px] h-[136px]' textClassName='text-xs mt-1' onCreate={() => { }} />
              {actors.map((actor, index) => {
                return (
                  <div className='w-[100px] h-[136px] relative'>
                    <ActorCard
                      key={`${actor.id}_${index}`}
                      actor={actor}
                      name={actor.name}
                      url={actor.video}
                      poster={actor.image}
                      // isSelected={selectedActorId === actor.id}
                      status={actor.generateStatus}
                      createdAt={actor.createdAt}
                      onClick={() => handleActorSelect(actor)}
                      onEdit={() => actor.video && handleVideoClick(actor.id)}
                      // onEdit={() => actor.video && handleVideoClick(index)}
                      // onRename={(name) => rename({ id: actor.id, name })}
                      onRegenerate={() => regenerateActor(actor)}
                      onDelete={() => deleteActor(actor.id)}

                    />
                  </div>
                );
              })}
            </div>
          </div>
          {modalOpen && (
            <EditVideoPlayModal
              open={modalOpen}
              videos={actors}
              initialIndex={selectedVideoIndex}
              onOpenChange={setModalOpen}
              voices={voices}
            />
          )}
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 border-t border-[rgba(233,234,235,1)]'>
          {/* <TextButton
            label={t('agent_dashboard.user_detail.template.all_actor_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_detail.template.all_actor_modal.save')}
            className='w-[135px] h-11'
            onClick={handleSave}
          /> */}
          <TextButton
            label={t('common.close')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
